import { OpenIDProvider } from '@salte-auth/salte-auth';
import { injectable, Container, LazyServiceIdentifer, inject, unmanaged } from "inversify";
import { injectToken, getToken, Token, TokenContainerModule } from "inversify-token";
import { YinzCamConfigServer, YinzCamConfigServerToken } from "yinzcam-config";
import { YinzCamInjectModule } from 'yinzcam-inject';
import { JanusModeContextManager, JanusModeContextManagerToken } from "./mode";
import { get, Readable, writable, Writable } from "svelte/store";
import { YinzCamSignonServer, YinzCamSignonServerToken } from 'yinzcam-signon';
import { YinzCamDSPServerToken, YinzCamDSPServer } from 'yinzcam-signon';
import { AbstractReactiveMicrocomponent, ControlBase, DeadlineTimer, ManualPassthrough, OutputMapper, ReactiveMicrocomponent, ReactiveMicrocomponentConfig, SimpleComponent, SimpleSink } from 'yinzcam-rma';
import { YinzCamAPIRequestParameterComponent, YinzCamAPIRequestParameterComponentToken, YinzCamAPIRequestParameters, YinzCamAPIResponse } from 'yinzcam-api';
import { ycSSOAuthFinished, ycTicket } from './ycapp';
import { SalteAuth } from '@salte-auth/salte-auth';
import { Tab } from '@salte-auth/tab';
import Axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { YinzCamDropsServer, YinzCamDropsServerToken } from 'yinzcam-drops';
import { isNil } from 'lodash';

export const JanusSignonManagerToken = new Token<JanusSignonManager>(Symbol.for("JanusSignonManager"));

const JanusSignonStateManagerToken = new Token<JanusSignonStateManager>(Symbol.for("JanusSignonStateManager"));

export let JanusSignonManagerModule: YinzCamInjectModule = new YinzCamInjectModule((container: Container): void => {
  container.load(new TokenContainerModule((bindToken) => {
    bindToken(JanusSignonStateManagerToken).to(JanusSignonStateManager).inSingletonScope();
    bindToken(JanusSignonManagerToken).to(JanusSignonManager).inSingletonScope();
    bindToken(YinzCamAPIRequestParameterComponentToken).to(JanusSignonManagerParameterComponent).inSingletonScope();
  }));
});

export interface SSOResult {
  success: boolean;
  data?: any;
  errorCode?: string;
  errorMessage?: { 
    title: string; 
    description: string
  };
}

export interface SignonStatus {
  loggedIn: boolean;
  ticket?: string;
  expiration?: number;
  validSpan?: number;
  yinzId?: string;
  accountCreated?: boolean;
  dropsTicket?: string;
};

const INTERNAL_ERROR: SSOResult = {
  success: false,
  errorCode: 'INTERNAL_ERROR',
  errorMessage: {
    title: 'Internal Error',
    description: 'An internal error occurred.'
  }
};

export interface SignonPasswordlessOptions {
  EmailTemplateSlug: string;
  EmailTemplateHtml?: string;
  EmailTemplateUrl?: string;
};

// this is stored outside of the manager class to avoid circular dependencies with the parameter component
@injectable()
class JanusSignonStateManager {
  private readonly statusComponent: ManualPassthrough<SignonStatus>;
  private readonly expirationTimeComponent: ReactiveMicrocomponent<number>;
  private readonly expirationTimerComponent: ReactiveMicrocomponent<number>;
  private readonly expirationHandlerComponent: ReactiveMicrocomponent<void>;

  public constructor() {
    this.statusComponent = new ManualPassthrough({ name: 'JanusSignonStatusComponent', saveToLocalStorage: true }, { loggedIn: false });
    this.expirationTimeComponent = new OutputMapper(
      { name: 'JanusSignonStatusExpirationTimeComponent', saveToLocalStorage: false }, this.statusComponent,
      ($status) => {
        if (!$status?.loggedIn || !$status.expiration) {
          return 0x7FFFFFFF;
        }
        return $status.expiration;
      });
    this.expirationTimerComponent = new DeadlineTimer('JanusSignonStatusExpirationTimerComponent', this.expirationTimeComponent);
    this.expirationHandlerComponent = new SimpleSink(
      { name: 'JanusSignonStatusExpirationHandlerComponent', saveToLocalStorage: false },
      async ($timer: number) => {
        if (!$timer || $timer < this.getStatus().expiration) {
          console.log('JanusSignonStatusExpirationHandlerComponent: timer fired but not logged in or expiration not passed', $timer);
          return;
        }
        console.log('JanusSignonStatusExpirationHandlerComponent: timer fired, logging out');
        this.statusComponent.setValue({ loggedIn: false });
      }, this.expirationTimerComponent);
  }

  public getStatusComponent(): ReactiveMicrocomponent<SignonStatus> {
    return this.statusComponent;
  }

  public getStatus(): SignonStatus {
    return this.statusComponent.getValue();
  }

  public updateTicketStatus(status: SignonStatus) {
    if (!status?.loggedIn || !status?.ticket || !status?.expiration) {
      console.log('JanusSignonStateManager.updateTicketStatus: not logged in or invalid ticket, setting logged out');
      this.statusComponent.setValue({ loggedIn: false });
      return;
    }
    // for testing expiration logout
    //status.expiration = Date.now() + 90000;
    console.log('JanusSignonStateManager: status updated', status);
    this.statusComponent.setValue(status);
  }
}

@injectable()
export class JanusSignonManager {
  private readonly appId: string;

  public constructor (
    @injectToken(JanusModeContextManagerToken) private readonly modeManager: JanusModeContextManager,
    @injectToken(YinzCamSignonServerToken) private readonly signonServer: YinzCamSignonServer,
    @injectToken(YinzCamDSPServerToken) private readonly dspServer: YinzCamDSPServer,
    @injectToken(YinzCamConfigServerToken) private readonly configServer: YinzCamConfigServer,
    @injectToken(JanusSignonStateManagerToken) private readonly stateManager: JanusSignonStateManager,
    @injectToken(YinzCamDropsServerToken) private readonly dropServer: YinzCamDropsServer) {
    this.appId = `${CONFIG.league}_${CONFIG.tricode}`.toUpperCase();

    console.log('JanusSignonManager: subscribing to ycapp.ycTicket store');
    (ycTicket as Readable<{ticket: string, validSpan: number}>).subscribe((ti) => {
      console.log('ticket: ', ti)
      if (!ti || !ti.ticket || !ti.validSpan) {
        return;
      }
      console.log('JanusSignonManager: updating ticket status from ycapp');
      setTimeout(async () => {
        const dropsTicketInfo = await this.getDropsTicket(ti.ticket);
        this.stateManager.updateTicketStatus({
          loggedIn: true,
          ticket: ti.ticket,
          expiration: parseInt(dropsTicketInfo.ExpireTime), // use drops ticket expiration
          validSpan: parseInt(dropsTicketInfo.ValidSpan), // use drops ticket valid span
          yinzId: dropsTicketInfo.YinzId,
          dropsTicket: dropsTicketInfo.Ticket,
        });
      }, 0);
    });
  }

  public getStatusComponent(): ReactiveMicrocomponent<SignonStatus> {
    return this.stateManager.getStatusComponent();
  }

  public getStatus(): SignonStatus {
    return this.stateManager.getStatus();
  }

  public logout(): SignonStatus {
    this.stateManager.updateTicketStatus(null);
    return this.stateManager.getStatus();
  }

  //Alumni chat
  private async getDropsTicket(ssoTicket: string) {
    let data = { "UMS": { "Ticket": ssoTicket } };
    let dropTicket: any = await this.dropServer.getDropTicket(data).then(res => res?.data);
    //console.log('dropTicket: ', dropTicket);
    //this.dropServer.setDropTicket(dropTicket);
    return dropTicket;
  }

  /*
  <TicketResponse>
	<Ticket>+n2I1dH3Q22T0Zb85VSbZwAAAXnjpnikhFEO+itsfbW4U9pa/X4VQcgpxS5RkRL7grCGAr61s1DIyziZP2o0xjH92dVZK0atCB/Wjy2mh20C/zb0OkOTq6JpOZBq/oXSXeM0UFNDS+UPdJZIdLijDpodtRFGk/3a</Ticket>
	<IssueTime>1623022008484</IssueTime>
	<ExpireTime>1626622008484</ExpireTime>
	<ValidSpan>3600000000</ValidSpan>
	<YinzId>b8b1eff1-f91b-4872-ad43-e0c98b3d131a</YinzId>
	<AccountCreated>true</AccountCreated>
</TicketResponse>
*/

  /*
  <RestErrorResponse>
	<ErrorCode>DSP_SERVER_ERROR</ErrorCode>
	<UserMessage>
		<Title>Registration Error</Title>
		<Content>Looks like you've already signed up with this email.  Try logging in with your email and password.</Content>
	</UserMessage>
</RestErrorResponse>
*/

  private makeErrorResult(data: any): SSOResult {
    if (data?.ErrorCode) {
      // TODO: make this a common utility function
      return {
        success: false,
        data,
        errorCode: data.ErrorCode,
        errorMessage: {
          title: data.UserMessage?.Title || 'Error',
          description: data.UserMessage?.Content || 'An error occurred.'
        }
      }
    } else {
      return { ...INTERNAL_ERROR, data };
    }
  }

  private async handleTicketGrantingAPIResponse(rsp: YinzCamAPIResponse): Promise<SSOResult> {
    if (!rsp?.data) {
      return INTERNAL_ERROR;
    }
    let data = rsp.data as any;
    if (data.Ticket && data.ExpireTime) {
      const dropsTicketInfo = await this.getDropsTicket(data.Ticket);
      this.stateManager.updateTicketStatus({
        loggedIn: true,
        ticket: data.Ticket,
        expiration: parseInt(dropsTicketInfo.ExpireTime), // use drops ticket expiration
        validSpan: parseInt(dropsTicketInfo.ValidSpan), // use drops ticket valid span
        yinzId: data.YinzId,
        accountCreated: data.AccountCreated === 'true',
        dropsTicket: dropsTicketInfo.Ticket,
      });
      //this.userStateChanged = true;
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve({ success: true, data });
        }, 0);
      });
    } else {
      return this.makeErrorResult(data);
    }
  }

  private async handleGenericAPIResponse(rsp: YinzCamAPIResponse, expectData: boolean = true): Promise<SSOResult> {
    if (expectData && !rsp?.data) {
      return INTERNAL_ERROR;
    }
    
    const data = rsp.data as Record<string, any>;
    if (!rsp.isStatusNot2xx) {
      return { success: true, data };
    }

    if (data.ErrorCode || data.UserMessage) {
      return {
        success: false,
        errorCode: data.ErrorCode || 'GENERIC_ERROR',
        errorMessage: {
          title: data.UserMessage?.Title || 'Error',
          description: data.UserMessage?.Content || 'An error occurred.'
        }
      }
    }

    return INTERNAL_ERROR;
  }

  public async registerWithDSPUserPassword(user: string, password: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postRegister({
      Credentials: {
        DSPAccount: {
          AppId: this.appId,
          ClientId: CONFIG.dspClientId,
          DeviceId: await this.getDSPGuestId(),
          Username: user,
          Password: password
        }
      },
      Profile: {
        Entry: [{
          Key: 'locale',
          Value: navigator.language
        }]
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  private createMapFromProfileResponse(data: any): { [key: string]: string } {
    if (!data?.Entry) {
      return {};
    }
    let entries = data.Entry as { Key: string, Value: string}[];
    let dict = Object.fromEntries(entries.map(e => [e.Key, e.Value])); 
    return dict;
  }

  public async checkEmailAvailability(email: string): Promise<SSOResult> {
    const rsp = await this.signonServer.getRegisterAvailableRequest(email);
    const result = await this.handleGenericAPIResponse(rsp);
    if (result.success) {
      result.data = result.data.Available === 'true';
    }
    return result;
  }

  public async validateYinzCamUserPassword(user: string, password: string): Promise<SSOResult> {
    const rsp = await this.signonServer.postVerifyRequest({
      YinzCam: {
        AppId: this.appId,
        Identity: user,
        Passphrase: password
      }
    });
    const result = await this.handleGenericAPIResponse(rsp);
    if (result.success) {
      result.data = !!result.data.Verified;
    }
    return result;
  }

  public async loginWithYinzCamUserPassword(user: string, password: string): Promise<SSOResult> {
    const rsp = await this.signonServer.postTicket({
      YinzCam: {
        AppId: this.appId,
        Identity: user,
        Passphrase: password
      },
      CreateNewAccount: false
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithYinzCamPasswordless(user: string, otp: string, pwlessOptions?: SignonPasswordlessOptions): Promise<SSOResult> {
    const rsp = await this.signonServer.postTicket({
      YinzCam: {
        AppId: this.appId,
        Identity: user,
        Passphrase: null,
        OTP: otp,
      },
      CreateNewAccount: false,
      ...(pwlessOptions ? { PasswordlessOptions: pwlessOptions } : {})
    });
    return this.handleGenericAPIResponse(rsp);
  }

  public async registerWithYinzCamEmailPasswordWithOtp(email: string, password: string, otp: string, profile?: { [key: string]: string }, pwlessOptions?: SignonPasswordlessOptions): Promise<SSOResult> {
    profile = { ...profile, email };
    const entries = Object.entries(profile).filter(e => !isNil(e[1])).map(e => ({ Key: e[0], Value: e[1] }));
    const rsp = await this.signonServer.postRegister({
      Credentials: {
        AppId: this.appId,
        Identity: email,
        Passphrase: password,
        OTP: otp,
      },
      Profile: {
        Entry: entries,
      },
      ...(pwlessOptions ? { PasswordlessOptions: pwlessOptions } : {})
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async registerWithYinzCamEmailPassword(email: string, password: string, profile?: { [key: string]: string }): Promise<SSOResult> {
    profile = { ...profile, email };
    const entries = Object.entries(profile).filter(e => !isNil(e[1])).map(e => ({ Key: e[0], Value: e[1] }));
    let rsp = await this.signonServer.postRegister({
      Credentials: {
        AppId: this.appId,
        Identity: email,
        Passphrase: password
      },
      Profile: {
        Entry: entries
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async validatePasswordlessToken(token: string): Promise<SSOResult> {
    const rsp = await this.signonServer.getPasswordlessTokenValidate(token);
    const result = await this.handleGenericAPIResponse(rsp, false);
    //console.log('validatePasswordlessToken', token, result);
    return result;
  }

  public async pollPasswordlessStatus(otp: string): Promise<SSOResult> {
    const rsp = await this.signonServer.getPasswordlessStatusPoll(otp);
    // note that the response may not include ticket data
    const result = await this.handleTicketGrantingAPIResponse(rsp);
    //console.log('pollPasswordlessStatus', otp, result);
    return result;
  }

  public async resendPasswordlessEmail(otp: string): Promise<SSOResult> {
    const rsp = await this.signonServer.getPasswordlessResend(otp);
    const result = await this.handleGenericAPIResponse(rsp, false);
    //console.log('resendPasswordlessEmail', otp, result);
    return result;
  }

  // NOTE: this method is for the new password reset flow that uses a magic link emailed to the user
  public async requestPasswordReset(email: string, password: string, otp: string, pwlessOptions?: SignonPasswordlessOptions): Promise<SSOResult> {
    const rsp = await this.signonServer.postPasswordResetRequest({
      YinzCam: {
        AppId: this.appId,
        Identity: email,
        Passphrase: password,
        OTP: otp,
      },
      ...(pwlessOptions ? { PasswordlessOptions: pwlessOptions } : {})
    });
    return this.handleGenericAPIResponse(rsp);
  }

  // NOTE: these methods are for the legacy password reset flow that uses a PIN number
  public async passwordResetBegin(email: string): Promise<SSOResult> {
    const rsp = await this.signonServer.postPasswordResetBegin({ Identity: email });
    return await this.handleGenericAPIResponse(rsp);
  }

  public async passwordResetValidate(email: string, pin: string): Promise<SSOResult> {
    const rsp = await this.signonServer.postPasswordResetValidate({ Identity: email, PIN: pin });
    return await this.handleGenericAPIResponse(rsp);
  }

  public async passwordResetEnd(email: string, pin: string, password: string): Promise<SSOResult> {
    const rsp = await this.signonServer.postPasswordResetEnd({ Identity: email, PIN: pin, Passphrase: password });
    return await this.handleGenericAPIResponse(rsp);
  }

  private async getProfileSegment(segment: string): Promise<SSOResult> {
    let rsp = await this.signonServer.getProfile2UserSegment(this.getStatus().ticket, segment);
    console.log('GET PROFILE SEGMENT', segment, rsp);
    let data = rsp?.data as any;
    return { success: !rsp.isStatusNot2xx, data: this.createMapFromProfileResponse(data) };
  }

  // public async getUserProfileSegment(): Promise<SSOResult> {
  //   let rsp = await this.signonServer.getProfile2UserSegment('USER');
  //   let data = rsp?.data as any;
  //   return { success: !rsp.isStatusNot2xx, data: this.createMapFromProfileResponse(data) };
  // }

  private getProfileSegmentComponent(segment: string): ReactiveMicrocomponent<{ [key: string]: string }> {
    let ic = this.signonServer.getProfile2UserSegmentComponent('DSPACCOUNT');
    let name = `sso:getProfileSegmentComponent:${segment}`;
    return new OutputMapper({ name, saveToLocalStorage: false }, ic, (rsp) => {
      return this.createMapFromProfileResponse(rsp?.data);
    });
  }

  private async updateProfileSegment(segment: string, profile: { [key: string]: string }): Promise<SSOResult> {
    let entries = Object.entries(profile).map(e => ({ Key: e[0], Value: e[1] }));
    let data = { Entry: entries };
    let rsp = await this.signonServer.postProfile2UserSegment(this.getStatus().ticket, segment, data);
    data = rsp?.data as any;
    if (!data.Entry) {
      return INTERNAL_ERROR;
    }
    return { success: true };
  }

  public updateUserProfileSegment(profile: { [key: string]: string }): Promise<SSOResult> {
    return this.updateProfileSegment('USER', profile);
  }

  public getDSPProfileSegment(): Promise<SSOResult> {
    return this.getProfileSegment('DSPACCOUNT');
  }

  public getDSPProfileSegmentComponent(): ReactiveMicrocomponent<{ [key: string]: string }> {
    return this.getProfileSegmentComponent('DSPACCOUNT');
  }

  public async checkDSPAccountVerification(): Promise<SSOResult> {
    let res = await this.getDSPProfileSegment();
    if (!res?.success) {
      return INTERNAL_ERROR;
    }
    return { success: res.data?.['verified'] === 'true' };
  }

  public updateDSPProfileSegment(profile: { [key: string]: string }): Promise<SSOResult> {
    return this.updateProfileSegment('DSPACCOUNT', profile);
  }

  public async uploadDSPProfileImage(image: Blob, name: string): Promise<SSOResult> {
    let formData = new FormData();
    formData.append('file', image, name);
    let rsp = await this.signonServer.postUploadImageSegment(this.getStatus().ticket, 'DSPAccount', formData);
    //console.log('uploadDSPProfileImage', rsp);
    return { success: true, data: rsp.data };
  }

  public async loginWithDSPUserPassword(user: string, password: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      DSPAccount: {
        AppId: this.appId,
        ClientId: CONFIG.dspClientId,
        DeviceId: await this.getDSPGuestId(),
        Username: user,
        Password: password
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithDSPAccessToken(accessToken: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      DSPAccount: {
        AppId: this.appId,
        ClientId: CONFIG.dspClientId,
        DeviceId: await this.getDSPGuestId(),
        AccessToken: accessToken
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  private createDSPSocialAuth(policy: string): SalteAuth {
    return new SalteAuth({      
      providers: [
        new LaLigaDSPProvider({
          baseURL: 'https://prelogin.laliga.es',
          tenantID: 'LaLigaDspPreB2C.onmicrosoft.com',
          clientID: CONFIG.dspClientId,
          policy,
        })
      ],
      handlers: [ new Tab({ default: true }) ]
    });
  }

  private async doDSPSocialLogin(policy: string): Promise<SSOResult> {
    const auth = this.createDSPSocialAuth(policy);
    const result = await new Promise<SalteAuth.EventWrapper>((resolve, reject) => {
      auth.on('login', (error, data) => {
        if (error) {
          reject(error);
        } else {
          resolve(data);
        }
      });
      auth.login('laliga-dsp');
    });
    const provider = auth.provider(result.provider) as LaLigaDSPProvider;
    const token = await provider.exchangeCodeForToken();
    return this.loginWithDSPAccessToken(token);
  }

  public async loginWithDSPFacebook(): Promise<SSOResult> {
    return this.doDSPSocialLogin('b2c_1a_signupsignin_fb_nl');
  }

  public async loginWithDSPGoogle(): Promise<SSOResult> {
    return this.doDSPSocialLogin('b2c_1a_signupsignin_gl_nl');
  }

  public async loginWithRayadosUserPassword(email: string, password: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      Rayados: {
        AppId: this.appId,
        Email: email,
        Password: password
      },
      CreateNewAccount: false
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async passwordResetWithDSP(user: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postPasswordResetRequest({
      DSPAccount: {
        AppId: this.appId,
        ClientId: CONFIG.dspClientId,
        DeviceId: await this.getDSPGuestId(),
        Username: user
      }
    });
    if (!rsp || rsp.isStatusNot2xx) {
      return INTERNAL_ERROR;
    }
    return { success: true };
  }

  public getConsentsWithStatus(): ReactiveMicrocomponent<YinzCamAPIResponse> {
    // get or load guest ID
    //let guestId = await this.getDSPGuestId();

    // get consents from config server
    let configLegalComp = this.configServer.getConfigLegal();
    //console.log('GET CONSENTS', rsp);

    // wrap with our current consent status
    let consentStatusComp = new ConsentsWithStatusComponent({ name: 'JanusSignonManager.getConsentsWithStatus', saveToLocalStorage: false }, configLegalComp);

    return consentStatusComp;
  }

  // TODO: probably want this class to manage consentData?
  // this version of the function works with profile fields instead of consent IDs
  public async setConsentStatusOld(consentFlags: { [profileField: string]: boolean }, consentData: { version: string, consents: { id: string, profileField: string}[] }) {
    // store locally
    let consentStatus = { consentsVersion: consentData.version, consentFlags };
    localStorage.setItem('JanusSignonManager:ConsentStatusJson', JSON.stringify(consentStatus));

    // get our guest ID
    let dspGuestId = await this.getDSPGuestId();

    // accept policy
    await this.dspServer.postPoliciesAccept(dspGuestId, consentData.version, new Date());

    // accept consents
    // have to jump through all these hoops because "id" isn't always present on consents...
    let idConsentFlags = Object.fromEntries(consentData.consents.filter(c => c.id).map(c => [ c.id, consentFlags[c.profileField] || false ]));
    await this.dspServer.postConsentsStatus(dspGuestId, idConsentFlags);
  }

  public async setConsentStatus(consentFlags: { [consentId: string]: boolean }, consentData: { version: string, consents: { id: string, profileField: string}[] }) {
    // store locally
    let consentStatus = { consentsVersion: consentData.version, consentFlags };
    localStorage.setItem('JanusSignonManager:ConsentStatusJson', JSON.stringify(consentStatus));

    // get our guest ID
    let dspGuestId = await this.getDSPGuestId();

    try {
      // accept policy
      await this.dspServer.postPoliciesAccept(dspGuestId, "2.1" /*consentData.version*/, new Date());

      // accept consents
      // have to jump through all these hoops because "id" isn't always present on consents...
      await this.dspServer.postConsentsStatus(dspGuestId, consentFlags);
    } catch (e) {
      console.log("Unable to save consents", e);
    }
  }

  // I'm not sure if this even needs to be used?
  public async getDSPGuestId() {
    let guestId = localStorage.getItem('JanusSignonManager:DSPGuestId');
    if (!guestId) {
      // TODO: get country code from some API
      let countryCode = 'es';

      // Request a new guest ID from DSP
      let rsp = await this.dspServer.postRegisterGuest(countryCode);
      guestId = (rsp.data as any).guestId;
      localStorage.setItem('JanusSignonManager:DSPGuestId', guestId);
    }
    return guestId;
  }

  private randomCode(): string {
    // Use crypto if available, otherwise fallback to vanilla JS
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    if (globalThis.crypto && globalThis.crypto.getRandomValues) {
      const array = new Uint8Array(32);
      globalThis.crypto.getRandomValues(array);
      for (let i = 0; i < array.length; i++) {
        result += charset[array[i] % charset.length];
      }
    } else {
      for (let i = 0; i < 32; i++) {
        const randomIndex = Math.floor(Math.random() * charset.length);
        result += charset[randomIndex];
      }
    }
    return result;
  }

  private base64URLEncode(str: string): string {
    const b64 = btoa(str);
    const encoded = b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
    return encoded;
  }

  public generatePKCECodeVerifier(): string {
    const rando = this.randomCode();
    const encoded = this.base64URLEncode(rando);
    //console.log(`Code Verifier strings random: ${rando} | encoded: ${encoded}`);
    return encoded;
  }

  private async sha256(str: string): Promise<string>{
    // Use crypto.subtle if available, otherwise fallback to simple hash
    if (globalThis.crypto?.subtle?.digest) {
      const data = new TextEncoder().encode(str);
      const digestOp = await globalThis.crypto.subtle.digest({ name: "SHA-256" }, data);
      return this.bufferToBase64UrlEncoded(digestOp);
    } else {
      // Fallback: simple hash (not cryptographically secure)
      function simpleHash(str: string): number {
        let hash = 0, i, chr;
        if (str.length === 0) return hash;
        for (i = 0; i < str.length; i++) {
          chr = str.charCodeAt(i);
          hash = ((hash << 5) - hash) + chr;
          hash |= 0; // Convert to 32bit integer
        }
        return Math.abs(hash);
      }
      return Promise.resolve(this.base64URLEncode(simpleHash(str).toString()));
    }
  };
  
  private bufferToBase64UrlEncoded(hash: ArrayBuffer): string {
    // Convert ArrayBuffer to base64url string
    const uintArray = new Uint8Array(hash);
    let binary = '';
    for (let i = 0; i < uintArray.length; i++) {
      binary += String.fromCharCode(uintArray[i]);
    }
    const b64 = btoa(binary);
    return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
  };
  
  public async generatePKCEChallenge(codeVerifierString: string): Promise<string> {
    const sha = await this.sha256(codeVerifierString);
    console.log(`generated sha ${sha} for codeVerifier ${codeVerifierString}`);
    return sha;
  }
}

// Modifies a consent response to include local status info
class ConsentsWithStatusComponent extends AbstractReactiveMicrocomponent<YinzCamAPIResponse, [YinzCamAPIResponse]> {
  public constructor(config: ReactiveMicrocomponentConfig<any>, input: ReactiveMicrocomponent<YinzCamAPIResponse>) {
    super(config, input);
  }

  protected async update($control: unknown, $input: YinzCamAPIResponse): Promise<YinzCamAPIResponse> {
    let consentStatusJson = localStorage.getItem('JanusSignonManager:ConsentStatusJson');
    let consentStatus = { consentsVersion: "0.0", consentFlags: {} };
    if (consentStatusJson) {
      consentStatus = JSON.parse(consentStatusJson);
    }
    ($input.data as any).consentStatus = consentStatus;
    return $input;
  }
}

@injectable()
export class JanusSignonManagerParameterComponent extends AbstractReactiveMicrocomponent<YinzCamAPIRequestParameters, [ SignonStatus ]> implements YinzCamAPIRequestParameterComponent {
  public constructor(@injectToken(JanusSignonStateManagerToken) private readonly stateManager: JanusSignonStateManager) {
    super({ name: 'JanusSignonManagerParameterComponent', saveToLocalStorage: false }, stateManager.getStatusComponent());
  }

  protected async update($control: unknown, $status: SignonStatus): Promise<YinzCamAPIRequestParameters> {
    console.log("JanusSignonManagerParameterComponent dropsTicket: ", $status);
    if (!$status?.loggedIn || !$status?.ticket) {
      return {};
    }
    //console.log("JanusSignonManagerParameterComponent dropsTicket: " + $status.dropsTicket);
    let ret: YinzCamAPIRequestParameters = {
      services: {
        signon: {
          headers: {
            'X-YinzCam-Ticket': $status.ticket
          }
        }
      }
    };
    if ($status.dropsTicket) {
      ret.services.app = {
        headers: {
          'X-YinzCam-Ticket': $status.dropsTicket
        }
      }
    }
    return ret;
  }
}

export interface LaLigaDSP {
  config: LaLigaDSP.Config;
}

export declare namespace LaLigaDSP {
  interface Config extends OpenIDProvider.Config {
    baseURL?: string;
    tenantID: string;
    policy: string;
  }
}

export class LaLigaDSPProvider extends OpenIDProvider {
  constructor(config: LaLigaDSP.Config) {
    // fill in defaults
    config = {
      baseURL: 'https://login.microsoftonline.com',
      responseType: 'code',
      redirectUrl: new URL('/', window.location.origin).toString().slice(0, -1),
      ...config
    };
    // compute optional fields, if they are missing
    config = {
      url: `${config.baseURL}/${config.tenantID}/oauth2/v2.0`,
      scope: `openid ${config.clientID} offline_access`,
      ...config
    };
    super(config);
  }

  get name() {
    return 'laliga-dsp';
  }

  get login() {
    return this.url(`${this.config.url}/authorize`, {
      p: this.config.policy,
    });
  }

  get logout() {
    return this.url(`${this.config.url}/logout`, {
      p: this.config.policy,
    });
  }

  get token() {
    return this.url(`${this.config.url}/token`, {
      p: this.config.policy,
    });
  }

  public async exchangeCodeForToken(): Promise<string> {
    if (!this.code) {
      throw new Error('code must be available, call login first');
    }
    const params = new URLSearchParams();
    params.append('client_id', this.config.clientID);
    params.append('scope', this.config.scope);
    params.append('grant_type', 'authorization_code');
    params.append('code', this.code);
    const result = await Axios.post(this.token, params);
    return result.data.access_token;
  }
}